import { useId } from 'react';

import { AppResponsiveDialog, ErrorState, InlineNotice, LoadingState } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { notify } from '@/lib/toast';

import {
  useChangeNotificationPreference,
  useNotificationPreferenceQuery,
} from '../hooks/useNotificationPreference';

interface NotificationSettingsDialogProps {
  onClose: () => void;
}

/**
 * 알림 설정(specs/080 US4·081 US3) — 지원사업 사전 안내 메일 받기 켜기·끄기. PC 계정 메뉴와 모바일 상단바 알림 버튼이 같은 창을 연다
 * (PC 다이얼로그·모바일 하단 시트, `AppResponsiveDialog`). 문구는 회원 유형 중립(지도사·기업 회원 공통). 열 때만 그린다.
 * 관심 사업별 알림은 관심 사업 설정에서 따로 끈다(이 설정이 꺼지면 모두 멈춘다).
 */
export function NotificationSettingsDialog({ onClose }: NotificationSettingsDialogProps) {
  const checkboxId = useId();
  const query = useNotificationPreferenceQuery(true);
  const change = useChangeNotificationPreference();

  function onToggle(checked: boolean) {
    change.mutate(checked, {
      onSuccess: (data) => notify.success(data.enabled ? '알림을 켰어요.' : '알림을 껐어요.'),
    });
  }

  return (
    <AppResponsiveDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="알림 설정"
      description="등록한 업체에 맞는 지원사업의 공고 예정·공고·접수 일정을 메일로 알려 드려요."
      footer={
        <div className="flex w-full justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            닫기
          </Button>
        </div>
      }
    >
      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState onRetry={() => query.refetch()} />
      ) : (
        <div className="flex flex-col gap-3 py-1">
          <div className="flex items-start gap-3">
            <Checkbox
              id={checkboxId}
              checked={query.data.enabled}
              disabled={change.isPending}
              onCheckedChange={(v) => onToggle(v === true)}
            />
            <div className="flex flex-col gap-1">
              <Label htmlFor={checkboxId}>지원사업 사전 안내 메일 받기</Label>
              <p className="text-sm text-muted-foreground">
                다음 달 공고 예정 사업, 새로 올라온 공고, 관심 사업의 접수 시작·마감 임박을 알려
                드려요.
              </p>
            </div>
          </div>
          {!query.data.enabled && (
            <InlineNotice tone="warning">
              알림이 꺼져 있어요. 관심 사업의 접수 일정 알림도 받지 않아요.
            </InlineNotice>
          )}
        </div>
      )}
    </AppResponsiveDialog>
  );
}
